import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import {
  Video,
  Shield,
  Users,
  Lock,
  Globe,
  MessageSquare,
  ArrowRight,
  CheckCircle2,
  Monitor,
  Mic,
  Calendar,
  BarChart3,
  Star,
  Quote,
} from "lucide-react";

const stats = [
  { value: "10M+", label: "Meetings Hosted" },
  { value: "500K+", label: "Active Users" },
  { value: "99.9%", label: "Uptime" },
  { value: "150+", label: "Countries" },
];

const testimonials = [
  {
    name: "Sarah Johnson",
    role: "CEO, TechStart Inc.",
    content: "MeetHub has transformed how our remote team collaborates. The video quality is exceptional and the breakout rooms feature is a game-changer for our brainstorming sessions.",
    rating: 5,
  },
  {
    name: "Michael Chen",
    role: "Product Manager, InnovateCo",
    content: "We switched from Zoom to MeetHub and haven't looked back. The guest access feature makes it so easy for clients to join without creating accounts.",
    rating: 5,
  },
  {
    name: "Emily Rodriguez",
    role: "University Professor",
    content: "The live captions and recording features have made my online lectures more accessible to all students. Absolutely love the intuitive interface!",
    rating: 5,
  },
];

export default function Home() {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-20 pb-12 px-4 sm:pt-32 sm:pb-20 sm:px-6 lg:px-8">
        <div className="container mx-auto text-center">
          <div className="inline-block mb-3 px-3 py-1.5 sm:mb-4 sm:px-4 sm:py-2 rounded-full bg-primary/10 text-primary text-xs sm:text-sm font-medium">
            🚀 Professional Video Conferencing Made Simple
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent px-2">
            Connect, Collaborate, Create
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto px-2">
            MeetHub brings teams together with HD video meetings, real-time chat, breakout rooms, 
            and powerful collaboration tools. Join as a guest or host unlimited meetings.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center px-4">
            <Link to="/auth" className="w-full sm:w-auto">
              <Button size="lg" className="gap-2 w-full sm:w-auto">
                Start Free Meeting
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/features" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="w-full sm:w-auto">
                Explore Features
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-4 sm:py-16 sm:px-6 lg:px-8 bg-primary/5">
        <div className="container mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-2">
                  {stat.value}
                </div>
                <div className="text-sm sm:text-base text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-12 px-4 sm:py-20 sm:px-6 lg:px-8 bg-muted/50">
        <div className="container mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4 px-2">Why Choose MeetHub?</h2>
            <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto px-2">
              Everything you need for professional video meetings, from quick catch-ups to large webinars.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[
              {
                icon: Video,
                title: "HD Video & Audio",
                description: "Crystal-clear video with adjustable quality settings and advanced audio controls.",
              },
              {
                icon: Monitor,
                title: "Screen Sharing",
                description: "Share your screen with annotation tools for interactive presentations.",
              },
              {
                icon: Users,
                title: "Breakout Rooms",
                description: "Split participants into smaller groups for focused discussions and workshops.",
              },
              {
                icon: MessageSquare,
                title: "Live Chat & Q&A",
                description: "Real-time messaging, polls, and Q&A features to engage participants.",
              },
              {
                icon: Mic,
                title: "Live Captions",
                description: "Automatic speech-to-text captions for accessibility and easy follow-along.",
              },
              {
                icon: Shield,
                title: "Secure Meetings",
                description: "Password protection, waiting rooms, and host controls for safe collaboration.",
              },
            ].map((feature) => (
              <Card key={feature.title} className="p-4 sm:p-6 hover:shadow-lg transition-shadow">
                <div className="mb-3 sm:mb-4 p-2 sm:p-3 rounded-lg bg-primary/10 w-fit">
                  <feature.icon className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm sm:text-base text-muted-foreground">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 px-4 sm:py-20 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">
                Powerful Meeting Features
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mb-6 sm:mb-8">
                MeetHub provides everything you need to run professional meetings, 
                from small team syncs to large-scale webinars with hundreds of attendees.
              </p>
              <div className="space-y-3 sm:space-y-4">
                {[
                  "Virtual backgrounds & blur effects",
                  "Meeting recording & transcription",
                  "Webinar mode with raise hand feature",
                  "Polls & interactive Q&A sessions",
                  "Calendar integration (Google, Outlook)",
                  "Analytics & meeting insights",
                  "QR code sharing for easy joining",
                  "Guest access - no account required",
                ].map((benefit) => (
                  <div key={benefit} className="flex items-center gap-2 sm:gap-3">
                    <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
                    <span className="text-sm sm:text-base">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <Card className="p-4 sm:p-6 text-center">
                <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-primary mx-auto mb-2 sm:mb-3" />
                <div className="text-xl sm:text-2xl font-bold mb-1">Instant</div>
                <div className="text-xs sm:text-sm text-muted-foreground">Schedule or start now</div>
              </Card>
              <Card className="p-4 sm:p-6 text-center">
                <Lock className="h-6 w-6 sm:h-8 sm:w-8 text-primary mx-auto mb-2 sm:mb-3" />
                <div className="text-xl sm:text-2xl font-bold mb-1">Secure</div>
                <div className="text-xs sm:text-sm text-muted-foreground">Password & waiting room</div>
              </Card>
              <Card className="p-4 sm:p-6 text-center">
                <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-primary mx-auto mb-2 sm:mb-3" />
                <div className="text-xl sm:text-2xl font-bold mb-1">Analytics</div>
                <div className="text-xs sm:text-sm text-muted-foreground">Meeting insights</div>
              </Card>
              <Card className="p-4 sm:p-6 text-center">
                <Globe className="h-6 w-6 sm:h-8 sm:w-8 text-primary mx-auto mb-2 sm:mb-3" />
                <div className="text-xl sm:text-2xl font-bold mb-1">Anywhere</div>
                <div className="text-xs sm:text-sm text-muted-foreground">Web-based, no install</div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 px-4 sm:py-20 sm:px-6 lg:px-8 bg-muted/50">
        <div className="container mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4 px-2">
              Loved by Teams Worldwide
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto px-2">
              See what our customers have to say about their experience with MeetHub.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="p-6 relative">
                <Quote className="h-8 w-8 text-primary/20 absolute top-4 right-4" />
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: testimonial.rating }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-6 text-sm sm:text-base">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-primary font-semibold">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <div className="font-semibold text-sm">{testimonial.name}</div>
                    <div className="text-xs text-muted-foreground">{testimonial.role}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4 sm:py-20 sm:px-6 lg:px-8 bg-primary/5">
        <div className="container mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4 px-2">
            Ready to Start Meeting?
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto px-2">
            Create your free account and start hosting HD video meetings in seconds.
            Or join any meeting as a guest - no signup required.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <Link to="/auth" className="w-full sm:w-auto">
              <Button size="lg" className="gap-2 w-full sm:w-auto">
                Get Started Free
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/join" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="w-full sm:w-auto">
                Join a Meeting
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
